/**
 * useNotifications — realtime notification hook.
 *
 * Subscribes to Supabase Realtime for live notification
 * delivery. Returns unread count, notification list, and
 * actions (mark read, mark all read, archive, delete).
 * Auto-cleans subscription on unmount.
 */

import { useCallback, useEffect, useState } from 'react';
import { getSupabase } from '@/lib/supabase';
import { notificationService } from '@/lib/notification/NotificationService';
import { useToast } from '@/components/ui';
import type { NotificationRow } from '@/lib/notification/types';

export function useNotifications(userId: string | undefined) {
  const [notifications, setNotifications] = useState<NotificationRow[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const refresh = useCallback(async () => {
    if (!userId) return;
    const [list, count] = await Promise.all([
      notificationService.list(userId, { limit: 20 }),
      notificationService.getUnreadCount(userId),
    ]);
    setNotifications(list);
    setUnreadCount(count);
    setIsLoading(false);
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }
    void refresh();
  }, [userId, refresh]);

  useEffect(() => {
    if (!userId) return;
    const supabase = getSupabase();

    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const newNotif = payload.new as NotificationRow;
          setNotifications((prev) => [newNotif, ...prev].slice(0, 20));
          setUnreadCount((prev) => prev + 1);
          toast({
            variant: 'info',
            title: newNotif.title,
            description: newNotif.body,
          });
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          void refresh();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, refresh, toast]);

  const markRead = useCallback(async (notificationId: string) => {
    if (!userId) return;
    await notificationService.markRead(notificationId, userId);
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n)),
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, [userId]);

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    await notificationService.markAllRead(userId);
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })),
    );
    setUnreadCount(0);
  }, [userId]);

  const archive = useCallback(async (notificationId: string) => {
    if (!userId) return;
    await notificationService.archive(notificationId, userId);
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, [userId]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markRead,
    markAllRead,
    archive,
    refresh,
  };
}
